import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { MapPin, CircleDot, Trophy, Coins, Flag } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Countdown from "@/components/Countdown";
import AdBanner from "@/components/AdBanner";
import ShareTeamCard from "@/components/ShareTeamCard";
import LogoImage from "@/components/LogoImage";
import { gruppoColor } from "@/lib/race";
import PilotAvatar from "@/components/PilotAvatar";

const HERO_IMG =
  "https://images.unsplash.com/photo-1774353986758-d1b5f1151874?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxoaWxsY2xpbWIlMjByYWNlJTIwY2FyJTIwbW91bnRhaW4lMjByb2FkJTIwc3BlZWR8ZW58MHx8fHwxNzg5NTAxMTc4fDA&ixlib=rb-4.1.0&q=85";

function BigCountdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = Math.max(0, new Date(target).getTime() - now);
  const pad = (n) => String(n).padStart(2, "0");
  const parts = [
    { v: pad(Math.floor(diff / 86400000)), l: "GG" },
    { v: pad(Math.floor((diff % 86400000) / 3600000)), l: "HH" },
    { v: pad(Math.floor((diff % 3600000) / 60000)), l: "MM" },
    { v: pad(Math.floor((diff % 60000) / 1000)), l: "SS" },
  ];
  return (
    <div data-testid="market-countdown" className="inline-flex flex-col items-center gap-2 rounded-2xl border border-[#FF1E27]/40 bg-black/60 backdrop-blur-md px-5 py-4">
      <p className="font-body-sport text-[10px] sm:text-xs font-bold tracking-[0.3em] text-[#FFB703] uppercase">
        Chiusura Mercato tra
      </p>
      <div className="flex items-start gap-2 sm:gap-3">
        {parts.map((p) => (
          <div key={p.l} className="text-center">
            <span className="font-time font-black text-3xl sm:text-5xl text-white tabular-nums">{p.v}</span>
            <span className="block font-body-sport text-[9px] tracking-widest text-gray-400 mt-0.5">{p.l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function Dashboard() {
  const { user } = useAuth();
  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const { data: teamData } = useQuery({
    queryKey: ["my-team"],
    queryFn: async () => (await api.get("/team")).data,
    refetchInterval: 15000,
  });

  const race = settings?.race || {};
  const iscrizioniAperte = race.iscrizioni_close
    ? new Date(race.iscrizioni_close).getTime() > Date.now()
    : true;

  const team = teamData?.team;
  const pilots = teamData?.pilots || [];
  const teamSize = teamData?.team_size ?? 18;
  const teamComplete = !!team && pilots.length === teamSize;
  const branding = settings?.branding || {};
  const heroDesktop = branding.hero_image || HERO_IMG;
  const heroMobile = branding.hero_image_mobile || branding.hero_image || HERO_IMG;

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="relative overflow-hidden rounded-2xl border border-[#3F3F4C] min-h-[380px] flex items-end">
        <LogoImage
          src={heroDesktop}
          alt="Cronoscalata"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-55 hidden sm:block"
        />
        <LogoImage
          src={heroMobile}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-55 sm:hidden"
        />
        <div className="absolute inset-0 bg-black" style={{ opacity: Math.max(0.35, (branding.hero_overlay ?? 0) / 100) }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121214] via-[#121214]/60 to-transparent" />
        <div className="absolute top-0 left-0 right-0 checkered-strip opacity-80" />
        <div className="relative z-10 p-6 sm:p-10 w-full">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span
              data-testid="hero-location-badge"
              className="flex items-center gap-1.5 font-body-sport text-[10px] sm:text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border border-[#3F3F4C] bg-[#1A1A1E]/80 backdrop-blur text-gray-300"
            >
              <MapPin className="w-3 h-3 text-[#FF1E27]" />
              {race.location || "Chiaramonte Gulfi (RG)"}
            </span>
            <span
              data-testid="hero-status-badge"
              className={`flex items-center gap-1.5 font-body-sport text-[10px] sm:text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border backdrop-blur ${
                iscrizioniAperte
                  ? "border-[#22C55E]/50 bg-[#22C55E]/10 text-[#22C55E]"
                  : "border-[#FF1E27]/50 bg-[#FF1E27]/10 text-[#FF1E27]"
              }`}
            >
              <CircleDot className="w-3 h-3 live-dot" />
              {iscrizioniAperte ? "Iscrizioni Aperte" : "Iscrizioni Chiuse"}
            </span>
            {branding.race_logo_url && (
              <LogoImage
                src={branding.race_logo_url}
                alt="Logo ufficiale competizione"
                data-testid="hero-race-logo"
                hideOnError
                className="h-10 object-contain rounded bg-white/10 px-1.5 py-1 backdrop-blur"
              />
            )}
          </div>
          <h1 className="font-racing font-black text-3xl sm:text-4xl lg:text-5xl uppercase tracking-wider text-white drop-shadow-[0_2px_10px_rgba(255,30,39,0.3)] leading-tight">
            FantasyRace <span className="text-[#FF1E27]">69°</span>
            <br />
            Salita dei Monti Iblei
          </h1>
          <div className="mt-6">
            <BigCountdown target={race.lock_at || race.iscrizioni_close} />
          </div>
        </div>
      </div>

      <div
        data-testid="team-cta"
        className={`relative overflow-hidden rounded-2xl border-2 p-6 sm:p-8 text-center ${
          teamComplete ? "border-[#22C55E]/50 bg-[#22C55E]/5" : "border-[#FF1E27]/60 bg-[#FF1E27]/5"
        }`}
      >
        <p className="font-racing font-black text-xl sm:text-2xl uppercase tracking-wide text-white">
          {teamComplete ? "✅ Scuderia Confermata e Salvata!" : "⚠️ Scuderia Incompleta"}
        </p>
        <p className="text-sm text-gray-400 font-body-sport mt-1">
          {teamComplete
            ? `${pilots.length}/${teamSize} piloti schierati — sei pronto per la salita`
            : `Seleziona i tuoi ${teamSize} piloti! Ne hai schierati ${pilots.length}/${teamSize}`}
        </p>
        <Link
          to="/mercato"
          data-testid="team-cta-button"
          className={`inline-block mt-5 rounded-xl px-8 py-4 min-h-[48px] font-racing font-black text-sm tracking-widest text-white uppercase transition-colors ${
            teamComplete ? "bg-[#2A2A32] hover:bg-[#3F3F4C] border border-[#22C55E]/50" : "bg-[#FF1E27] hover:bg-[#CC1118]"
          }`}
        >
          {teamComplete ? "Modifica Schieramento ➔" : "Completa la tua squadra ➔"}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5">
              <div className="flex items-center gap-2 text-gray-400 font-body-sport text-[10px] font-bold tracking-widest uppercase">
                <Flag className="w-3.5 h-3.5 text-[#FF1E27]" /> Scuderia
              </div>
              <p data-testid="dash-team-name" className="font-racing font-bold text-white text-lg mt-2 truncate">
                {team?.nome_squadra || user?.team_name || "—"}
              </p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span
                  data-testid="team-status-badge"
                  className={`font-body-sport text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full border ${
                    team && pilots.length === (teamData?.team_size ?? 18)
                      ? "border-[#22C55E]/50 bg-[#22C55E]/10 text-[#22C55E]"
                      : "border-[#FF1E27]/50 bg-[#FF1E27]/10 text-[#FF1E27]"
                  }`}
                >
                  {team && pilots.length === (teamData?.team_size ?? 18) ? "Squadra Confermata" : "Squadra Incompleta"}
                </span>
                <span className="text-xs text-gray-500">
                  {team ? `${pilots.length}/${teamData?.team_size ?? 18} piloti schierati` : "Squadra non ancora creata"}
                </span>
              </div>
            </div>
            <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5">
              <div className="flex items-center gap-2 text-gray-400 font-body-sport text-[10px] font-bold tracking-widest uppercase">
                <Coins className="w-3.5 h-3.5 text-[#FFB703]" /> Crediti Residui
              </div>
              <p data-testid="dash-credits" className="font-time font-bold text-[#FFB703] text-2xl mt-2">
                {teamData?.crediti_residui ?? 120}
                <span className="text-xs text-gray-500"> /120</span>
              </p>
              <div className="h-1.5 rounded-full bg-[#121214] mt-2 overflow-hidden">
                <div
                  className="h-full bg-[#FFB703]"
                  style={{ width: `${Math.min(100, ((teamData?.costo || 0) / 120) * 100)}%` }}
                />
              </div>
            </div>
            <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5">
              <div className="flex items-center gap-2 text-gray-400 font-body-sport text-[10px] font-bold tracking-widest uppercase">
                <Trophy className="w-3.5 h-3.5 text-[#FF1E27]" /> Punteggio Totale
              </div>
              <p data-testid="dash-total-points" className="font-time font-bold text-white text-2xl mt-2">
                {user?.total_points ?? 0}
                <span className="text-xs text-gray-500"> pt</span>
              </p>
              <Link to="/classifica" className="text-xs text-[#FF1E27] hover:underline mt-1 inline-block">
                Vedi classifica
              </Link>
            </div>
          </div>

          {!team ? (
            <div className="rounded-xl border border-dashed border-[#FF1E27]/50 bg-[#FF1E27]/5 p-10 text-center">
              <Flag className="w-8 h-8 text-[#FF1E27] mx-auto mb-3" />
              <h3 className="font-racing font-bold text-white uppercase tracking-wide">
                La tua scuderia è ai box
              </h3>
              <p className="text-gray-400 text-sm mt-2 mb-5">
                Hai 120 crediti per schierare 18 piloti — 1 per ogni Gruppo — e un Capitano. Il mercato è aperto.
              </p>
              <Link
                to="/mercato"
                data-testid="dash-go-market"
                className="inline-block rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-6 py-3 font-body-sport font-bold text-xs tracking-widest text-white uppercase"
              >
                Vai al Mercato Piloti
              </Link>
            </div>
          ) : (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="font-racing font-bold text-xl uppercase tracking-wide text-white border-l-4 border-[#FF1E27] pl-3">
                  Formazione in Gara
                </h2>
                <ShareTeamCard teamData={teamData} user={user} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {pilots.map((p) => {
                  const gc = gruppoColor(p.gruppo);
                  const r = p.risultato || {};
                  const prove = r.punti_prove || 0;
                  const gara = (r.punti_gara || 0);
                  const extra = r.punti_eventi || 0;
                  const eff = p.is_captain ? Math.round((r.punti_totali || 0) * 15) / 10 : r.punti_totali || 0;
                  return (
                    <div
                      key={p.id}
                      data-testid={`dash-pilot-${p.id}`}
                      className={`driver-card relative rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-4 ${
                        p.is_captain ? "captain-card" : ""
                      }`}
                    >
                      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: gc }} />
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <PilotAvatar pilot={p} size={40} />
                          <div className="min-w-0">
                            <p className="font-body-sport font-semibold text-white text-sm truncate">
                              {p.nome} {p.cognome}
                            </p>
                            <p className="text-[11px] text-gray-500 truncate">{p.vettura}</p>
                          </div>
                        </div>
                        {p.is_captain && (
                          <span className="shrink-0 font-body-sport text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#FFB703] text-black">
                            CAP 1.5X
                          </span>
                        )}
                      </div>
                      <div className="flex items-end justify-between mt-3 pt-3 border-t border-[#3F3F4C]/60">
                        <div className="text-[10px] font-body-sport text-gray-500 space-y-0.5">
                          <p>
                            Prove: <span className={`font-time ${prove >= 0 ? "text-gray-300" : "text-[#EF4444]"}`}>{prove >= 0 ? prove : prove}</span>
                          </p>
                          <p>
                            Gara: <span className="font-time text-gray-300">{gara}</span>
                          </p>
                          <p>
                            Extra: <span className={`font-time ${extra >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>{extra >= 0 ? `+${extra}` : extra}</span>
                          </p>
                        </div>
                        <span className="font-time font-bold text-[#FFB703] text-lg">{eff} pt</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4">
                <Link
                  to="/mercato"
                  data-testid="dash-edit-team"
                  className="inline-block rounded-lg border border-[#FF1E27] text-[#FF1E27] hover:bg-[#FF1E27]/10 transition-colors px-5 py-2.5 font-body-sport font-bold text-xs tracking-widest uppercase"
                >
                  Modifica Formazione
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <AdBanner slot="sidebar" />
          <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5">
            <h3 className="font-racing font-bold text-sm uppercase tracking-wide text-white border-l-4 border-[#FFB703] pl-3 mb-4">
              Bacheca Trofei
            </h3>
            {user?.badges?.length ? (
              <div className="space-y-2" data-testid="trophy-case">
                {user.badges.map((b) => (
                  <div key={b.key} className="flex items-center gap-2.5 text-sm text-gray-300">
                    <span className="text-lg">{b.icona}</span>
                    <span className="font-body-sport font-semibold">{b.nome}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Nessun trofeo ancora conquistato. I badge vengono assegnati dopo la pubblicazione dei punteggi!
              </p>
            )}
          </div>
          <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5">
            <h3 className="font-racing font-bold text-sm uppercase tracking-wide text-white border-l-4 border-[#FFB703] pl-3 mb-4">
              Regole Rapide
            </h3>
            <ul className="space-y-2 text-xs text-gray-400 font-body-sport">
              <li className="flex justify-between"><span>1° Assoluto</span><span className="font-time text-white">50 pt</span></li>
              <li className="flex justify-between"><span>1° di Classe</span><span className="font-time text-white">20 pt</span></li>
              <li className="flex justify-between"><span>Miglioramento M2</span><span className="font-time text-[#22C55E]">+5 pt</span></li>
              <li className="flex justify-between"><span>Ritiro / DNF</span><span className="font-time text-[#EF4444]">-10 pt</span></li>
              <li className="flex justify-between"><span>Capitano</span><span className="font-time text-[#FFB703]">x1.5</span></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
